<mat-toolbar class="topbar admin-dark-topbar">
  <span>CSR Admin Console</span>
  <span class="spacer"></span>
  <mat-chip [highlighted]="true" class="mr-[0.5rem]">{{ auth.isAdmin() ? 'Admin' : 'Authenticated User' }}</mat-chip>
  <ng-container *ngIf="auth.isAdmin()">
    <button mat-stroked-button type="button" [matMenuTriggerFor]="supportMenu">
      <mat-icon class="!text-black">support_agent</mat-icon>
      Support
    </button>
    <mat-menu #supportMenu="matMenu">
      <a mat-menu-item *ngFor="let link of supportLinks" [routerLink]="link.path">{{ link.label }}</a>
    </mat-menu>
    <button mat-stroked-button type="button" [matMenuTriggerFor]="toolsMenu">
      <mat-icon class="!text-black">build</mat-icon>
      Tools
    </button>
    <mat-menu #toolsMenu="matMenu">
      <a mat-menu-item *ngFor="let link of toolsLinks" [routerLink]="link.path">{{ link.label }}</a>
    </mat-menu>
  </ng-container>
  <button mat-stroked-button type="button" (click)="logout()">Logout</button>
</mat-toolbar>

<main class="page">
  <mat-card class="hero">
    <h1>Authenticated User View</h1>
    <p>Admin-only sections are enabled based on JWT role claims.</p>
  </mat-card>

  <mat-progress-bar *ngIf="loading" mode="indeterminate"></mat-progress-bar>

  <div class="alerts-container" *ngIf="activeAlerts.length > 0">
    <mat-card class="alert-card" *ngFor="let alert of activeAlerts">
      <div class="alert-content">
        <div class="alert-text">
          <span class="alert-title">{{ alert.title }}</span>
          <p class="alert-body">{{ alert.body }}</p>
          <small class="alert-time">{{ alert.createdAt | date:'short' }}</small>
        </div>
        <div class="alert-actions">
          <button mat-button (click)="snoozeAlert(alert.id)" matTooltip="Snooze for 24h">Snooze</button>
          <button mat-button color="warn" (click)="dismissAlert(alert.id)">Dismiss</button>
        </div>
      </div>
    </mat-card>
  </div>

  <mat-tab-group>
    <mat-tab label="Insights" *ngIf="auth.isAdmin()">
      <app-analytics-dashboard></app-analytics-dashboard>
    </mat-tab>

    <mat-tab label="Announcements" *ngIf="auth.isAdmin()">
      <app-announcements-page [embedded]="true"></app-announcements-page>
    </mat-tab>

    <mat-tab label="My Profile">
      <section class="grid">
        <mat-card>
          <h2>Update Username</h2>
          <form [formGroup]="updateUsernameForm" class="form-grid">
            <mat-form-field appearance="outline">
              <mat-label>New Email</mat-label>
              <input matInput type="email" formControlName="newEmail" />
            </mat-form-field>
            <button mat-flat-button type="button" (click)="updateUsername()"
              [disabled]="updateUsernameForm.invalid || loading">
              Update
            </button>
          </form>
        </mat-card>

        <mat-card>
          <h2>Change Password</h2>
          <form [formGroup]="changePasswordForm" class="form-grid">
            <mat-form-field appearance="outline">
              <mat-label>Old Password</mat-label>
              <input matInput type="password" formControlName="oldPassword" />
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>New Password</mat-label>
              <input matInput type="password" formControlName="newPassword" />
            </mat-form-field>
            <button mat-flat-button type="button" (click)="changePassword()"
              [disabled]="changePasswordForm.invalid || loading">
              Change
            </button>
          </form>
        </mat-card>
      </section>
    </mat-tab>

    <mat-tab label="Partner Account" *ngIf="auth.isAdmin()">
      <section class="grid">
        <mat-card>
          <h2>Create Partner Account</h2>
          <p>Creates a university/company Account and optional portal login (Partner Login).</p>
          <form [formGroup]="createPartnerForm" class="form-grid">
            <mat-form-field appearance="outline">
              <mat-label>Account name</mat-label>
              <input matInput formControlName="name" />
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Slug (optional)</mat-label>
              <input matInput formControlName="slug" />
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Account type</mat-label>
              <mat-select formControlName="accountType">
                <mat-option value="university">University</mat-option>
                <mat-option value="company">Company</mat-option>
              </mat-select>
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Contact email (optional)</mat-label>
              <input matInput type="email" formControlName="contactEmail" />
            </mat-form-field>
            <mat-checkbox formControlName="openEnrollment">Open enrollment</mat-checkbox>
            <mat-checkbox formControlName="createPortalUser">Create portal login</mat-checkbox>
            <ng-container *ngIf="createPartnerForm.controls.createPortalUser.value">
              <mat-form-field appearance="outline">
                <mat-label>Portal user name</mat-label>
                <input matInput formControlName="portalName" />
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Portal user email</mat-label>
                <input matInput type="email" formControlName="portalEmail" />
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Portal user password</mat-label>
                <input matInput type="password" formControlName="portalPassword" />
              </mat-form-field>
            </ng-container>
            <button mat-flat-button type="button" (click)="createPartnerAccount()"
              [disabled]="createPartnerForm.invalid || loading">
              Create partner account
            </button>
          </form>
        </mat-card>
      </section>
    </mat-tab>

    <mat-tab label="Admin User" *ngIf="auth.isAdmin()">
      <section class="grid">
        <mat-card>
          <h2>Create Admin User</h2>
          <form [formGroup]="createAdminForm" class="form-grid">
            <mat-form-field appearance="outline">
              <mat-label>Name</mat-label>
              <input matInput formControlName="name" />
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Email</mat-label>
              <input matInput type="email" formControlName="email" />
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Password</mat-label>
              <input matInput type="password" formControlName="password" />
            </mat-form-field>
            <button mat-flat-button type="button" (click)="createAdminUser()"
              [disabled]="createAdminForm.invalid || loading">
              Create
            </button>
          </form>
        </mat-card>
      </section>
    </mat-tab>

    <mat-tab label="Career Role" *ngIf="auth.isAdmin()">
      <section class="grid">
        <mat-card>
          <h2>Career Role Console</h2>
          <p>Unified hub for role list, skills, coverage, and background job tracking.</p>
          <button mat-flat-button type="button" (click)="openRoleListPage()" [disabled]="loading">
            Open Career Role Console
          </button>
        </mat-card>

        <mat-card>
          <h2>Career Challenges</h2>
          <p>Create and manage role-scoped challenges, sponsors, teams, and repo submissions.</p>
          <button mat-flat-button type="button" (click)="openCareerChallengesPage()" [disabled]="loading">
            Open Career Challenges
          </button>
        </mat-card>

        <mat-card>
          <h2>Skill De-duplication</h2>
          <p>Review potential duplicate skills identified by daily background job.</p>
          <button mat-flat-button type="button" (click)="openSkillDeDuplicationPage()" [disabled]="loading">
            Open De-duplication
          </button>
        </mat-card>

        <mat-card>
          <h2>Archived Skills</h2>
          <p>Review skills hidden by the unused-skill sweep and restore any that should be active again.</p>
          <button mat-flat-button type="button" (click)="openArchivedSkillsPage()" [disabled]="loading">
            Open Archived Skills
          </button>
        </mat-card>

        <mat-card>
          <h2>Resource Management</h2>
          <p>Review draft resources and publish them to the main table.</p>
          <button mat-flat-button type="button" (click)="openResourceManagementPage()" [disabled]="loading">
            Open Management View
          </button>
        </mat-card>

        <mat-card>
          <h2>YouTube Language Reconcile</h2>
          <p>
            Re-fetch YouTube metadata for published and draft video resources, detect real language
            (API + title heuristics), and archive tutorials outside the configured allowlist
            (<strong>en</strong>, <strong>hi</strong> by default). Run preview first.
          </p>
          <form [formGroup]="reconcileYouTubeLanguagesForm" class="form-grid">
            <mat-form-field appearance="outline">
              <mat-label>Page Size</mat-label>
              <input matInput type="number" formControlName="pageSize" />
              <mat-hint>Resources scanned per API batch (max 500).</mat-hint>
            </mat-form-field>
            <mat-checkbox formControlName="publishedOnly">Published only (skip draft / review_required)</mat-checkbox>
            <div class="action-group">
              <button mat-stroked-button type="button" (click)="reconcileYouTubeLanguages(true)"
                [disabled]="reconcileYouTubeLanguagesForm.invalid || loading">
                Preview (Dry Run)
              </button>
              <button mat-flat-button color="warn" type="button" (click)="reconcileYouTubeLanguages(false)"
                [disabled]="reconcileYouTubeLanguagesForm.invalid || loading">
                Archive Vernacular Videos
              </button>
            </div>
          </form>
          <div *ngIf="lastYouTubeLanguageReconcile" class="reconcile-summary">
            <p *ngIf="lastYouTubeLanguageReconcile.runId" class="muted">
              Background run: {{ lastYouTubeLanguageReconcile.runId }}
            </p>
            <p>
              Last run: scanned {{ lastYouTubeLanguageReconcile.scanned }},
              kept {{ lastYouTubeLanguageReconcile.kept }},
              archived {{ lastYouTubeLanguageReconcile.archived }},
              errors {{ lastYouTubeLanguageReconcile.errors }}
            </p>
            <p *ngIf="lastYouTubeLanguageReconcile.archivedResourceIds.length" class="muted">
              Archived IDs: {{ lastYouTubeLanguageReconcile.archivedResourceIds.join(', ') }}
            </p>
            <p *ngIf="lastYouTubeLanguageReconcile.errorMessages.length" class="error">
              {{ lastYouTubeLanguageReconcile.errorMessages.join(' | ') }}
            </p>
          </div>
        </mat-card>

        <mat-card>
          <h2>Catalog Collection Monitor</h2>
          <p>Start and monitor provider catalog collection runs for LeetCode, HackerRank, and Kaggle.</p>
          <button mat-flat-button type="button" (click)="openCatalogCollectionPage()" [disabled]="loading">
            Open Catalog Monitor
          </button>
        </mat-card>

        <mat-card>
          <h2>Zero-Resource Sweep</h2>
          <p>Run zero-resource skill sweep and AI URL seeding with background job monitoring.</p>
          <button mat-flat-button type="button" (click)="openZeroResourceSweepPage()" [disabled]="loading">
            Open Sweep Console
          </button>
        </mat-card>

        <mat-card>
          <h2>Incomplete background jobs</h2>
          <p>View runs that are not completed or failed, and re-enqueue Hangfire when a job bailed on concurrency but stayed queued.</p>
          <button mat-flat-button type="button" (click)="openBackgroundJobsPage()" [disabled]="loading">
            Open job recovery
          </button>
        </mat-card>

        <mat-card>
          <h2>Latest Sweep Status</h2>
          <p>Quick health view for the latest zero-resource sweep run.</p>
          <div class="status-badge-grid">
            <div class="status-badge-item">
              <div class="status-badge-label">Zero-Resource Sweep</div>
              <ng-container *ngIf="latestSweepRun; else noSweepRun">
                <mat-chip [style.background-color]="runStatusColor(latestSweepRun.status)" style="color: white;">
                  {{ latestSweepRun.status }}
                </mat-chip>
                <div class="status-badge-meta">Run: {{ latestSweepRun.id }}</div>
              </ng-container>
              <ng-template #noSweepRun>
                <mat-chip>No run found</mat-chip>
              </ng-template>
            </div>
          </div>

          <div class="status-badge-actions">
            <button mat-stroked-button type="button" (click)="refreshLatestIngestionBadges()" [disabled]="loading">
              Refresh Status
            </button>
            <span *ngIf="latestIngestionBadgeUpdatedAt" class="status-badge-updated">
              Updated: {{ latestIngestionBadgeUpdatedAt | date:'shortTime' }}
            </span>
          </div>
        </mat-card>

        <mat-card>
          <h2>Canonical lessons</h2>
          <p>Browse draft, published, and archived lesson packs. Publish drafts into learner assignment or archive packs out of rotation.</p>
          <button mat-flat-button type="button" (click)="openLessonsPage()" [disabled]="loading">
            Open lessons
          </button>
        </mat-card>

        <mat-card>
          <h2>Add Specialization</h2>
          <form [formGroup]="addSpecializationForm" class="form-grid">
            <mat-form-field appearance="outline">
              <mat-label>Career Role Slug</mat-label>
              <input matInput formControlName="roleSlug" />
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Specialization Name</mat-label>
              <input matInput formControlName="name" />
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Slug (optional)</mat-label>
              <input matInput formControlName="slug" />
            </mat-form-field>
            <button mat-flat-button type="button" (click)="addSpecialization()"
              [disabled]="addSpecializationForm.invalid || loading">
              Add
            </button>
          </form>
        </mat-card>

        <mat-card>
          <h2>Populate Skills</h2>
          <form [formGroup]="populateSkillsForm" class="form-grid">
            <mat-form-field appearance="outline">
              <mat-label>Role Slugs (comma-separated)</mat-label>
              <textarea matInput rows="2" formControlName="careerRoleSlugs"></textarea>
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Locations (comma-separated)</mat-label>
              <input matInput formControlName="locations" />
            </mat-form-field>
            <mat-checkbox formControlName="forceRefresh">Force refresh</mat-checkbox>
            <mat-checkbox formControlName="updateExistingJobAspects">Update existing job aspects</mat-checkbox>
            <button mat-flat-button type="button" (click)="populateSkills()"
              [disabled]="populateSkillsForm.invalid || loading">
              Start Run
            </button>
          </form>
        </mat-card>

        <mat-card>
          <h2>Rebalance Skill Taxonomy</h2>
          <p>
            Moves track-specific core skills into specialization focus skills and optionally creates a
            <strong>generalist---exploring</strong> track. Leave role slugs empty to process all active roles.
            When not a dry run, queues a background job and can chain specialization skill repopulation.
          </p>
          <form [formGroup]="rebalanceSkillTaxonomyForm" class="form-grid">
            <mat-form-field appearance="outline">
              <mat-label>Role Slugs (comma-separated, empty = all active)</mat-label>
              <textarea matInput rows="2" formControlName="careerRoleSlugs"></textarea>
            </mat-form-field>
            <mat-checkbox formControlName="dryRun">Dry run (preview only, no writes)</mat-checkbox>
            <mat-checkbox formControlName="createGeneralistSpec">Create generalist specialization</mat-checkbox>
            <mat-checkbox formControlName="repopulateAfterRebalance">Repopulate specialization skills after rebalance</mat-checkbox>
            <button mat-flat-button type="button" (click)="rebalanceSkillTaxonomy()"
              [disabled]="rebalanceSkillTaxonomyForm.invalid || loading">
              {{ rebalanceSkillTaxonomyForm.controls.dryRun.value ? 'Preview Rebalance' : 'Start Rebalance Run' }}
            </button>
          </form>
        </mat-card>

        <mat-card>
          <h2>Topic Dependency Graph Backfill</h2>
          <p>
            Queue LLM inference as a background job, review validated edges in
            <strong>stagedRoleTopicDependencies</strong>, then commit to role catalogs.
            Powers SGA topic order and learning plan scheduling.
          </p>
          <button mat-flat-button type="button" (click)="openTopicDependencyBackfillPage()" [disabled]="loading">
            Open Backfill Console
          </button>
        </mat-card>

        <mat-card>
          <h2>Populate Resources</h2>
          <p>Catalog-only providers: <strong>leet_code</strong>, <strong>hackerrank</strong>, <strong>kaggle</strong>. Collect their catalogs separately before population runs. By default this only <strong>stages</strong> new rows. Turn on <em>Auto enrich</em> to run enrichment in the same flow, or use <em>Enrich Staged Resources</em> (the server can also schedule enrichment when enough resources are pending).</p>
          <form [formGroup]="populateResourcesForm" class="form-grid">
            <mat-form-field appearance="outline">
              <mat-label>Role Slugs (comma-separated)</mat-label>
              <textarea matInput rows="2" formControlName="careerRoleSlugs"></textarea>
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Providers (comma-separated)</mat-label>
              <input matInput formControlName="providers" />
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Max Queries / Role</mat-label>
              <input matInput type="number" formControlName="maxQueriesPerRole" />
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Max Resources / Query</mat-label>
              <input matInput type="number" formControlName="maxResourcesPerQuery" />
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Max Estimated Hours</mat-label>
              <input matInput type="number" step="0.1" formControlName="maxEstimatedHours" />
              <mat-hint>Server tier mix: ~65% micro (5–20m), 25% focused, 10% deep; ceiling caps longest tier.</mat-hint>
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Recent Published (days)</mat-label>
              <input matInput type="number" formControlName="recentPublishedWithinDays" />
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Ingestion Grace (days)</mat-label>
              <input matInput type="number" formControlName="ingestionGracePeriodDays" />
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Publish Threshold</mat-label>
              <input matInput type="number" step="0.01" formControlName="publishThreshold" />
            </mat-form-field>
            <mat-checkbox formControlName="useAiEnrichment">Use AI enrichment</mat-checkbox>
            <mat-checkbox formControlName="autoEnrich">Auto enrich</mat-checkbox>
            <mat-checkbox formControlName="isCorrectionRun">Correction run</mat-checkbox>
            <mat-checkbox formControlName="skipRecentlyIngested">Skip recently ingested</mat-checkbox>
            <mat-checkbox formControlName="publishHighConfidence">Publish high confidence</mat-checkbox>
            <button mat-flat-button type="button" (click)="populateResources()"
              [disabled]="populateResourcesForm.invalid || loading">
              Start Resource Run
            </button>
          </form>
        </mat-card>

        <mat-card>
          <h2>Populate Verified Role Resources</h2>
          <p>Coverage-driven orchestrator job to ingest resources only for skills that haven't met the target verified count. By default it <strong>stages</strong> only; enable <em>Auto enrich</em> to enrich in the same job, or use scheduled / manual enrich.</p>
          <form [formGroup]="populateVerifiedRoleResourcesForm" class="form-grid">
            <mat-form-field appearance="outline">
              <mat-label>Role Slugs (comma-separated)</mat-label>
              <textarea matInput rows="2" formControlName="careerRoleSlugs"></textarea>
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Providers (comma-separated)</mat-label>
              <input matInput formControlName="providers" />
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Min Verified Res. / Skill</mat-label>
              <input matInput type="number" formControlName="minimumVerifiedResourcesPerSkill" />
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Max Skills to Process</mat-label>
              <input matInput type="number" formControlName="maxSkillsToProcess" />
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Max Resources / Query</mat-label>
              <input matInput type="number" formControlName="maxResourcesPerQuery" />
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Max Estimated Hours</mat-label>
              <input matInput type="number" step="0.1" formControlName="maxEstimatedHours" />
              <mat-hint>Server tier mix: ~65% micro (5–20m), 25% focused, 10% deep; ceiling caps longest tier.</mat-hint>
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Recent Published (days)</mat-label>
              <input matInput type="number" formControlName="recentPublishedWithinDays" />
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Ingestion Grace (days)</mat-label>
              <input matInput type="number" formControlName="ingestionGracePeriodDays" />
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Publish Threshold</mat-label>
              <input matInput type="number" step="0.01" formControlName="publishThreshold" />
            </mat-form-field>
            <mat-checkbox formControlName="useAiEnrichment">Use AI enrichment</mat-checkbox>
            <mat-checkbox formControlName="autoEnrich">Auto enrich</mat-checkbox>
            <mat-checkbox formControlName="isCorrectionRun">Correction run</mat-checkbox>
            <mat-checkbox formControlName="skipRecentlyIngested">Skip recently ingested</mat-checkbox>
            <button mat-flat-button type="button" (click)="populateVerifiedRoleResources()"
              [disabled]="populateVerifiedRoleResourcesForm.invalid || loading">
              Start Verified Resource Run
            </button>
          </form>
        </mat-card>

        <mat-card>
          <h2>Collect Provider Catalog</h2>
          <form [formGroup]="collectProviderCatalogForm" class="form-grid">
            <mat-form-field appearance="outline">
              <mat-label>Providers (comma-separated)</mat-label>
              <input matInput formControlName="providers" />
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Max Items / Provider</mat-label>
              <input matInput type="number" formControlName="maxItemsPerProvider" />
            </mat-form-field>
            <button mat-flat-button type="button" (click)="collectProviderCatalog()"
              [disabled]="collectProviderCatalogForm.invalid || loading">
              Start Catalog Collection
            </button>
          </form>
        </mat-card>

        <mat-card>
          <h2>Backfill Resource Prerequisites</h2>
          <p>
            Queues a Hangfire job that AI-infers prerequisites for resources that currently have none.
            Only skills on an <strong>active career-role graph</strong> are attached.
            Use Skill Run Status below to poll progress.
          </p>
          <form [formGroup]="backfillResourcePrerequisitesForm" class="form-grid">
            <mat-form-field appearance="outline">
              <mat-label>Resource status (optional)</mat-label>
              <mat-select formControlName="resourceStatus">
                <mat-option value="">Any</mat-option>
                <mat-option value="published">published</mat-option>
                <mat-option value="draft">draft</mat-option>
                <mat-option value="review_required">review_required</mat-option>
                <mat-option value="validation_failed">validation_failed</mat-option>
                <mat-option value="inactive">inactive</mat-option>
                <mat-option value="archived">archived</mat-option>
              </mat-select>
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Max resources</mat-label>
              <input matInput type="number" formControlName="maxResources" />
              <mat-hint>Resources with empty prerequisites to process (1–2000).</mat-hint>
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Scan page size</mat-label>
              <input matInput type="number" formControlName="scanPageSize" />
            </mat-form-field>
            <button
              mat-flat-button
              type="button"
              (click)="backfillResourcePrerequisites()"
              [disabled]="backfillResourcePrerequisitesForm.invalid || loading">
              Queue Prerequisites Backfill
            </button>
          </form>
        </mat-card>

        <mat-card>
          <h2>Refresh Job Aspects</h2>
          <form [formGroup]="refreshJobAspectsForm" class="form-grid">
            <mat-form-field appearance="outline">
              <mat-label>Role Slugs (comma-separated)</mat-label>
              <textarea matInput rows="2" formControlName="careerRoleSlugs"></textarea>
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Locations (comma-separated)</mat-label>
              <input matInput formControlName="locations" />
            </mat-form-field>
            <mat-checkbox formControlName="fullRefreshAllRoles">Full refresh all roles</mat-checkbox>
            <mat-checkbox formControlName="isCorrectionRun">Correction run</mat-checkbox>
            <button mat-flat-button type="button" (click)="refreshJobAspects()"
              [disabled]="refreshJobAspectsForm.invalid || loading">
              Refresh Job Aspects
            </button>
          </form>
        </mat-card>

        <mat-card>
          <h2>Enrich Resources</h2>
          <p class="mat-mdc-form-field-hint" style="margin: 0 0 0.5rem 0; max-width: 42rem">
            AI enrichment always processes all globally <code>pending</code> staged rows (Hangfire job). This calls
            <code>/resources/enrich</code>. Run ID is only used when AI is off (sync / commit flows).
          </p>
          <form [formGroup]="enrichResourcesForm" class="form-grid">
            <mat-form-field appearance="outline">
              <mat-label>Run ID (sync / non-AI only)</mat-label>
              <input matInput formControlName="runId" />
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Publish Threshold</mat-label>
              <input matInput type="number" step="0.01" formControlName="publishThreshold" />
            </mat-form-field>
            <mat-checkbox formControlName="useAiEnrichment">Use AI enrichment</mat-checkbox>
            <mat-checkbox formControlName="isCorrectionRun">Correction run</mat-checkbox>
            <button mat-flat-button type="button" (click)="enrichResources()"
              [disabled]="enrichResourcesForm.invalid || loading">
              Enrich Staged Resources
            </button>
          </form>
        </mat-card>

        <mat-card>
          <h2>Queue staging enrichment</h2>
          <p class="mat-mdc-form-field-hint" style="margin: 0 0 0.5rem 0; max-width: 42rem">
            Triggers a Hangfire job immediately. The scheduled job queues enrichment when <strong>any</strong> pending
            staged resources exist (and no enrichment job is already active). The job always pulls all pending rows globally.
          </p>
          <form [formGroup]="queueStagedResourceEnrichmentForm" class="form-grid">
            <mat-form-field appearance="outline">
              <mat-label>Publish Threshold</mat-label>
              <input matInput type="number" step="0.01" formControlName="publishThreshold" />
            </mat-form-field>
            <mat-checkbox formControlName="useAiEnrichment">Use AI enrichment</mat-checkbox>
            <mat-checkbox formControlName="isCorrectionRun">Correction run</mat-checkbox>
            <button mat-flat-button type="button" (click)="queueStagedResourceEnrichment()"
              [disabled]="queueStagedResourceEnrichmentForm.invalid || loading">
              Queue enrichment
            </button>
          </form>
        </mat-card>

        <mat-card>
          <h2>Commit enriched staged resources</h2>
          <p class="mat-mdc-form-field-hint" style="margin: 0 0 0.5rem 0; max-width: 42rem">
            Runs synchronously: promotes staged rows in <code>enriched</code> to committed resources. Calls
            <code>/resources/enrich/commit-enriched</code>. Leave <strong>Run ID</strong> empty to process all enriched
            rows globally (typical after a Bedrock batch completes).
          </p>
          <form [formGroup]="commitEnrichedStagedResourcesForm" class="form-grid">
            <mat-form-field appearance="outline">
              <mat-label>Run ID (optional — empty = all enriched)</mat-label>
              <input matInput formControlName="runId" />
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Publish Threshold</mat-label>
              <input matInput type="number" step="0.01" formControlName="publishThreshold" />
            </mat-form-field>
            <mat-checkbox formControlName="isCorrectionRun">Correction run</mat-checkbox>
            <button mat-flat-button type="button" (click)="commitEnrichedStagedResources()"
              [disabled]="commitEnrichedStagedResourcesForm.invalid || loading">
              Commit enriched
            </button>
          </form>
        </mat-card>

        <mat-card>
          <h2>Reprocess published resources (post graph)</h2>
          <p class="mat-mdc-form-field-hint" style="margin: 0 0 0.5rem 0; max-width: 42rem">
            After role graph work: stages published resources mapped to activation-eligible skills (skips in-flight
            staged rows), then runs correction AI enrichment and commit. Calls
            <code>/resources/reprocess-published</code>. Leave role slugs empty for all eligible roles.
          </p>
          <form [formGroup]="reprocessPublishedResourcesForm" class="form-grid">
            <mat-form-field appearance="outline">
              <mat-label>Role slugs (optional CSV)</mat-label>
              <input matInput formControlName="roleSlugs" />
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Publish Threshold</mat-label>
              <input matInput type="number" step="0.01" formControlName="publishThreshold" />
            </mat-form-field>
            <mat-checkbox formControlName="useAiEnrichment">Use AI enrichment</mat-checkbox>
            <mat-checkbox formControlName="strictActiveOnly">Strict active only (exclude activating)</mat-checkbox>
            <mat-checkbox formControlName="dryRun">Dry run</mat-checkbox>
            <button mat-flat-button type="button" (click)="reprocessPublishedResources()"
              [disabled]="reprocessPublishedResourcesForm.invalid || loading">
              Reprocess published resources
            </button>
          </form>
        </mat-card>

        <mat-card>
          <h2>Skill Run Status</h2>
          <form [formGroup]="getRunForm" class="form-grid">
            <mat-form-field appearance="outline">
              <mat-label>Run ID</mat-label>
              <input matInput formControlName="runId" />
            </mat-form-field>
            <button mat-flat-button type="button" (click)="getRun()" [disabled]="getRunForm.invalid || loading">
              Fetch
            </button>
          </form>
        </mat-card>

        <mat-card>
          <h2>Resource Run Status</h2>
          <form [formGroup]="getResourceRunForm" class="form-grid">
            <mat-form-field appearance="outline">
              <mat-label>Run ID</mat-label>
              <input matInput formControlName="runId" />
            </mat-form-field>
            <button mat-flat-button type="button" (click)="getResourceRun()"
              [disabled]="getResourceRunForm.invalid || loading">
              Fetch
            </button>
          </form>
        </mat-card>

        <mat-card>
          <h2>List Population Runs</h2>
          <form [formGroup]="listRunsForm" class="form-grid">
            <mat-form-field appearance="outline">
              <mat-label>Page</mat-label>
              <input matInput type="number" formControlName="page" />
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Page Size</mat-label>
              <input matInput type="number" formControlName="pageSize" />
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Status</mat-label>
              <mat-select formControlName="status">
                <mat-option value="">Any</mat-option>
                <mat-option *ngFor="let status of runStatuses" [value]="status">{{ status }}</mat-option>
              </mat-select>
            </mat-form-field>
            <button mat-flat-button type="button" (click)="listRuns()" [disabled]="listRunsForm.invalid || loading">
              List Runs
            </button>
          </form>
          <div *ngIf="listedRuns.length" class="run-list">
            <div *ngFor="let run of listedRuns" class="run-item">
              <div class="run-header">
                <strong>{{ run.id }}</strong>
                <span class="operation-badge">{{ run.operationType }}</span>
              </div>
              <div class="run-info">
                <span>{{ run.status }}</span>
                <mat-progress-bar mode="determinate" [value]="run.progressPercentage"></mat-progress-bar>
                <span>{{ run.progressPercentage | number:'1.0-0' }}%</span>
              </div>
              <div class="run-summary-inline" *ngIf="run.resourceSummary">
                <span matTooltip="Resources Created">📦 {{ run.resourceSummary.resourcesCreated }}</span>
                <span matTooltip="Skills Found" *ngIf="run.resourceSummary.skillsFound">🔑 {{
                  run.resourceSummary.skillsFound }}</span>
                <span matTooltip="Resources Enriched" *ngIf="run.resourceSummary.resourcesEnriched">✨ {{
                  run.resourceSummary.resourcesEnriched }}</span>
                <span matTooltip="Raw Candidates" *ngIf="run.resourceSummary.rawCandidatesFetched">🎯 {{
                  run.resourceSummary.rawCandidatesFetched }}</span>
                <span matTooltip="Post Quality" *ngIf="run.resourceSummary.candidatesAfterQualityFilter">✅ {{
                  run.resourceSummary.candidatesAfterQualityFilter }}</span>
                <span matTooltip="Post Dedup" *ngIf="run.resourceSummary.candidatesAfterDeduplication">🧩 {{
                  run.resourceSummary.candidatesAfterDeduplication }}</span>
                <span matTooltip="Post Existing Check" *ngIf="run.resourceSummary.candidatesAfterExistingCheck">🆕 {{
                  run.resourceSummary.candidatesAfterExistingCheck }}</span>
                <span matTooltip="Post Pre-Enrichment" *ngIf="run.resourceSummary.candidatesAfterPreEnrichmentValidation">🧪 {{
                  run.resourceSummary.candidatesAfterPreEnrichmentValidation }}</span>
              </div>
              <div class="run-summary-inline" *ngIf="run.skillSummary">
                <span matTooltip="Skills Created">🔑 {{ run.skillSummary.skillsCreated }}</span>
                <span matTooltip="Aspects Populated">🏷️ {{ run.skillSummary.jobAspectsPopulated }}</span>
              </div>
            </div>
          </div>
        </mat-card>

        <mat-card *ngIf="lastRun">
          <h2>Latest Run Snapshot</h2>
          <div *ngIf="lastRun" class="run-details">
            <p><strong>Status:</strong> {{ lastRun.status }}</p>
            <p><strong>Operation:</strong> {{ lastRun.operationType }}</p>
            <p><strong>Progress:</strong> {{ lastRun.progressPercentage | number:'1.0-0' }}%</p>

            <div *ngIf="lastRun.resourceSummary" class="summary-stats">
              <mat-divider></mat-divider>
              <h3>Resource Summary</h3>
              <div class="stats-grid">
                <div class="stat-item" *ngIf="lastRun.resourceSummary.rolesRequested">
                  <span class="label">Roles Requested:</span>
                  <span class="value">{{ lastRun.resourceSummary.rolesRequested }}</span>
                </div>
                <div class="stat-item" *ngIf="lastRun.resourceSummary.rolesFound">
                  <span class="label">Roles Found:</span>
                  <span class="value">{{ lastRun.resourceSummary.rolesFound }}</span>
                </div>
                <div class="stat-item" *ngIf="lastRun.resourceSummary.skillsRequested">
                  <span class="label">Skills Requested:</span>
                  <span class="value">{{ lastRun.resourceSummary.skillsRequested }}</span>
                </div>
                <div class="stat-item" *ngIf="lastRun.resourceSummary.skillsFound">
                  <span class="label">Skills Found:</span>
                  <span class="value">{{ lastRun.resourceSummary.skillsFound }}</span>
                </div>
                <div class="stat-item" *ngIf="lastRun.resourceSummary.totalResourcesStaged">
                  <span class="label">Total Staged:</span>
                  <span class="value">{{ lastRun.resourceSummary.totalResourcesStaged }}</span>
                </div>
                <div class="stat-item">
                  <span class="label">Raw Candidates:</span>
                  <span class="value">{{ lastRun.resourceSummary.rawCandidatesFetched }}</span>
                </div>
                <div class="stat-item">
                  <span class="label">After Quality Filter:</span>
                  <span class="value">{{ lastRun.resourceSummary.candidatesAfterQualityFilter }}</span>
                </div>
                <div class="stat-item">
                  <span class="label">After Dedup:</span>
                  <span class="value">{{ lastRun.resourceSummary.candidatesAfterDeduplication }}</span>
                </div>
                <div class="stat-item">
                  <span class="label">After Existing Check:</span>
                  <span class="value">{{ lastRun.resourceSummary.candidatesAfterExistingCheck }}</span>
                </div>
                <div class="stat-item">
                  <span class="label">After Pre-Enrichment:</span>
                  <span class="value">{{ lastRun.resourceSummary.candidatesAfterPreEnrichmentValidation }}</span>
                </div>
                <div class="stat-item" *ngIf="lastRun.resourceSummary.resourcesEnriched">
                  <span class="label">Enriched:</span>
                  <span class="value">{{ lastRun.resourceSummary.resourcesEnriched }}</span>
                </div>
                <div class="stat-item highlight">
                  <span class="label">Created/Populated:</span>
                  <span class="value">{{ lastRun.resourceSummary.resourcesCreated }}</span>
                </div>
                <div class="stat-item">
                  <span class="label">Mappings:</span>
                  <span class="value">{{ lastRun.resourceSummary.mappingsCreated }}</span>
                </div>
              </div>
            </div>

            <div *ngIf="lastRun.skillSummary" class="summary-stats">
              <mat-divider></mat-divider>
              <h3>Skill Summary</h3>
              <div class="stats-grid">
                <div class="stat-item">
                  <span class="label">Roles Requested:</span>
                  <span class="value">{{ lastRun.skillSummary.rolesRequested }}</span>
                </div>
                <div class="stat-item">
                  <span class="label">Skills Created:</span>
                  <span class="value">{{ lastRun.skillSummary.skillsCreated }}</span>
                </div>
                <div class="stat-item">
                  <span class="label">Aspects:</span>
                  <span class="value">{{ lastRun.skillSummary.jobAspectsPopulated }}</span>
                </div>
              </div>
            </div>

            <div *ngIf="lastRun.backfillResourcePrerequisitesSummary" class="summary-stats">
              <mat-divider></mat-divider>
              <h3>Prerequisites Backfill Summary</h3>
              <div class="stats-grid">
                <div class="stat-item">
                  <span class="label">Scanned:</span>
                  <span class="value">{{ lastRun.backfillResourcePrerequisitesSummary.scannedResources }}</span>
                </div>
                <div class="stat-item">
                  <span class="label">Targeted:</span>
                  <span class="value">{{ lastRun.backfillResourcePrerequisitesSummary.targetedResources }}</span>
                </div>
                <div class="stat-item">
                  <span class="label">Updated:</span>
                  <span class="value">{{ lastRun.backfillResourcePrerequisitesSummary.updatedResources }}</span>
                </div>
                <div class="stat-item">
                  <span class="label">Skipped:</span>
                  <span class="value">{{ lastRun.backfillResourcePrerequisitesSummary.skippedNoSuggestion }}</span>
                </div>
                <div class="stat-item">
                  <span class="label">Failed:</span>
                  <span class="value">{{ lastRun.backfillResourcePrerequisitesSummary.failedResources }}</span>
                </div>
              </div>
              <p *ngIf="lastRun.backfillResourcePrerequisitesSummary.errors.length" class="error">
                {{ lastRun.backfillResourcePrerequisitesSummary.errors.join(' | ') }}
              </p>
            </div>
          </div>
        </mat-card>
      </section>
    </mat-tab>

    <mat-tab label="Promotions" *ngIf="auth.isAdmin()">
      <section class="grid">
        <mat-card>
          <h2>List Promotions</h2>
          <form [formGroup]="listPromotionsForm" class="form-grid">
            <mat-form-field appearance="outline">
              <mat-label>Code</mat-label>
              <input matInput formControlName="code" />
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Trigger Type</mat-label>
              <mat-select formControlName="triggerType">
                <mat-option value="">Any</mat-option>
                <mat-option *ngFor="let t of triggerTypes" [value]="t">{{ t }}</mat-option>
              </mat-select>
            </mat-form-field>
            <button mat-flat-button type="button" (click)="listPromotions()" [disabled]="loading">
              Apply
            </button>
          </form>
          <div *ngIf="listedPromotions.length" class="promotion-list">
            <div *ngFor="let promo of listedPromotions" class="promo-item" (click)="selectPromotion(promo)"
              [class.selected]="selectedPromotion?.id === promo.id">
              <p><strong>{{ promo.name }}</strong> ({{ promo.code || 'NO CODE' }})</p>
              <p>{{ promo.triggerType }} | {{ promo.type }} | {{ promo.value }} | Active: {{ promo.isActive }}</p>
            </div>
          </div>
        </mat-card>

        <mat-card>
          <h2>{{ selectedPromotion ? 'Edit' : 'Create' }} Promotion</h2>
          <form [formGroup]="promotionForm" class="form-grid">
            <mat-form-field appearance="outline">
              <mat-label>Name</mat-label>
              <input matInput formControlName="name" />
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Code</mat-label>
              <input matInput formControlName="code" />
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Trigger Type</mat-label>
              <mat-select formControlName="triggerType">
                <mat-option *ngFor="let t of triggerTypes" [value]="t">{{ t }}</mat-option>
              </mat-select>
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Type</mat-label>
              <mat-select formControlName="type">
                <mat-option *ngFor="let pt of promotionTypes" [value]="pt">{{ pt }}</mat-option>
              </mat-select>
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Value</mat-label>
              <input matInput type="number" formControlName="value" />
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Eligibility</mat-label>
              <mat-select formControlName="eligibility">
                <mat-option *ngFor="let e of eligibilityTypes" [value]="e">{{ e }}</mat-option>
              </mat-select>
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Applicable Service Types</mat-label>
              <mat-select formControlName="applicableServiceTypes" multiple>
                <mat-option *ngFor="let s of serviceTypes" [value]="s">{{ s }}</mat-option>
              </mat-select>
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Valid From</mat-label>
              <input matInput type="date" formControlName="validFrom" />
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Valid Until</mat-label>
              <input matInput type="date" formControlName="validUntil" />
            </mat-form-field>
            <mat-checkbox formControlName="isActive">Is Active</mat-checkbox>
            <div class="actions">
              <button mat-flat-button color="primary" type="button" (click)="savePromotion()"
                [disabled]="promotionForm.invalid || loading">
                {{ selectedPromotion ? 'Update' : 'Create' }}
              </button>
              <button *ngIf="selectedPromotion" mat-button type="button" (click)="resetPromotionForm()">
                Clear / New
              </button>
            </div>
          </form>
        </mat-card>
      </section>
    </mat-tab>

    <mat-tab label="System & Cache" *ngIf="auth.isAdmin()">
      <section class="grid">
        <mat-card>
          <h2>Backup Jobs</h2>
          <p>Manually trigger background maintenance tasks.</p>
          <div class="actions">
            <button mat-flat-button type="button" (click)="triggerMongoBackup()" [disabled]="loading">
              Trigger MongoDB Backup
            </button>
            <button mat-flat-button type="button" (click)="triggerAutomatedSkillResourcePopulation()" [disabled]="loading">
              Run automated skill resource population
            </button>
          </div>
        </mat-card>

        <mat-card>
          <h2>Migrate Career Catalog (Stage → Production)</h2>
          <p>
            Copies <strong>active</strong> career roles, <strong>active</strong> specializations, and
            <strong>active</strong> seniority profiles from stage,
            plus their core/focus skills (with topics), graph dependency skills, and
            <strong>published resources mapped only to active-role core/focus skills</strong> (and those mappings).
            Resource prerequisites are kept only when the skill sits on an active career-role graph.
            Role/specialization account allowlists are remapped by account slug (stage → production).
            Production learner metrics on resources/mappings are preserved. Natural-key collisions
            (same slug / provider id, different <code>_id</code>) fail the run.
            Connection strings must include a database name; both databases are pinged first (including dry run).
            Credentials are cleared from the job record when it finishes.
            Does not sync deactivations/deletes or migrate accounts.
          </p>
          <form [formGroup]="migrateCareerCatalogForm" class="form-grid">
            <mat-form-field appearance="outline">
              <mat-label>Stage connection string</mat-label>
              <input matInput type="password" formControlName="stageConnectionString" autocomplete="off" />
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Production connection string</mat-label>
              <input matInput type="password" formControlName="productionConnectionString" autocomplete="off" />
            </mat-form-field>
            <mat-checkbox formControlName="dryRun">Dry run (count only, no writes)</mat-checkbox>
            <div class="actions">
              <button
                mat-flat-button
                type="button"
                (click)="migrateCareerCatalog()"
                [disabled]="migrateCareerCatalogForm.invalid || loading">
                {{ migrateCareerCatalogForm.controls.dryRun.value ? 'Queue Dry Run' : 'Queue Migration' }}
              </button>
            </div>
          </form>
        </mat-card>

        <mat-card>
          <h2>Cache Stats</h2>
          <button mat-flat-button type="button" (click)="getCacheStats()" [disabled]="loading">Get Stats</button>
        </mat-card>

        <mat-card>
          <h2>Clear All Cache</h2>
          <button mat-flat-button type="button" (click)="clearAllCache()" [disabled]="loading">Clear All</button>
        </mat-card>

        <mat-card>
          <h2>Clear Cache by Pattern</h2>
          <form [formGroup]="clearPatternForm" class="form-grid">
            <mat-form-field appearance="outline">
              <mat-label>Pattern</mat-label>
              <input matInput formControlName="pattern" />
            </mat-form-field>
            <button mat-flat-button type="button" (click)="clearPattern()"
              [disabled]="clearPatternForm.invalid || loading">
              Clear Pattern
            </button>
          </form>
        </mat-card>

        <mat-card>
          <h2>Clear Cache by Key</h2>
          <form [formGroup]="clearKeyForm" class="form-grid">
            <mat-form-field appearance="outline">
              <mat-label>Key</mat-label>
              <input matInput formControlName="key" />
            </mat-form-field>
            <button mat-flat-button type="button" (click)="clearKey()" [disabled]="clearKeyForm.invalid || loading">
              Clear Key
            </button>
          </form>
        </mat-card>
        <mat-card>
          <h2>Clear Dashboard Cache</h2>
          <p>Deletes analytics aggregation snapshots.</p>
          <button mat-flat-button type="button" (click)="clearDashboardCache()" [disabled]="loading">Clear Dashboard</button>
        </mat-card>
      </section>
    </mat-tab>
  </mat-tab-group>

  <mat-card *ngIf="!auth.isAdmin()" class="notice">
    <h2>Admin Role Required</h2>
    <p>Your session is authenticated but does not include the admin role claim.</p>
  </mat-card>

  <mat-card class="response">
    <h2>Response Viewer</h2>
    <p *ngIf="lastError" class="error">{{ lastError }}</p>
    <pre>{{ lastResponse }}</pre>
  </mat-card>
</main>
